/**
 * WardsPendingConsents
 *
 * Banner para ACUDIENTES (guardian): muestra los check-ins/consentimientos
 * pendientes de las personas a su cargo (wards) y permite completarlos desde
 * su propia cuenta. Reutiliza `DirectorConsentLauncher` (carga member+person
 * por la inscripción y abre el `ConsentCheckinModal`, que ya soporta la firma
 * del padre/acudiente para menores).
 *
 * Se auto-oculta si el usuario no tiene wards con consentimiento pendiente.
 * Pensado para el dashboard, de cara al campamento de Aventureros (los padres
 * hacen todo el proceso de sus hijos).
 */
import { useEffect, useRef, useState } from "react";
import { AlertCircle, FileText, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { getWardsOf } from "@/lib/services/persons";
import { eventService, eventRegistrationService } from "@/lib/services/events";
import {
  getConsentById,
  isConsentCompleted,
} from "@/lib/services/eventConsents";
import DirectorConsentLauncher from "./DirectorConsentLauncher";
import type { Event, EventRegistration, Person } from "@/types";

interface WardItem {
  ward: Person;
  registration: EventRegistration;
  event: Event;
}

export default function WardsPendingConsents({
  className,
  onLoaded,
}: {
  className?: string;
  /** Avisa si esta cuenta tiene menores a cargo, para que quien la incruste
   *  pueda cambiar su propio mensaje (el dashboard le dice "acudiente" en vez
   *  de "sin rol asignado"). */
  onLoaded?: (tieneACargo: boolean) => void;
}) {
  const { profile, user } = useAuthStore();
  const [items, setItems] = useState<WardItem[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // En una ref para no meter la callback en las dependencias del efecto (si
  // el padre la recrea en cada render, el efecto entraría en bucle).
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  // Recargar cuando un consentimiento se actualiza (lo dispara el modal).
  useEffect(() => {
    const handler = () => setReloadTick((t) => t + 1);
    window.addEventListener("consent:updated", handler);
    return () => window.removeEventListener("consent:updated", handler);
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!user?.uid) {
        setItems([]);
        return;
      }
      try {
        const wards = await getWardsOf(user.uid);
        const eventCache = new Map<string, Event | null>();
        const results: WardItem[] = [];
        for (const ward of wards) {
          const regs = await eventRegistrationService.getByPerson(ward.id);
          const activeRegs = regs.filter(
            (r) => r.status !== "cancelled" && r.status !== "rejected",
          );
          for (const reg of activeRegs) {
            if (!reg.memberId) continue;
            const consent = await getConsentById(reg.eventId, reg.memberId);
            if (!eventCache.has(reg.eventId)) {
              eventCache.set(
                reg.eventId,
                await eventService.getById(reg.eventId),
              );
            }
            const event = eventCache.get(reg.eventId);
            if (isConsentCompleted(consent, event?.consentChecklistConfig))
              continue;
            if (!event) continue;
            results.push({ ward, registration: reg, event });
          }
        }
        if (active) {
          setItems(results);
          // Se avisa por tener menores A CARGO, no por tenerlos pendientes:
          // el papá que ya completó todo sigue siendo acudiente y no debe ver
          // el aviso de "sin rol asignado".
          onLoadedRef.current?.(wards.length > 0);
        }
      } catch (err) {
        console.error("[WardsPendingConsents] load error", err);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [user?.uid, reloadTick]);

  if (items.length === 0) return null;
  // (el aviso al padre se emite en el efecto de carga, no aquí)

  const associationName = profile?.scopeName ?? "Asociación";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 shadow-sm ${className ?? ""}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-blue-700" />
            <p className="text-sm font-bold text-blue-900">
              Check-in pendiente de{" "}
              {items.length === 1
                ? "una persona a tu cargo"
                : "personas a tu cargo"}
            </p>
          </div>
          <p className="text-xs text-blue-800 mb-3">
            Completa el consentimiento y los documentos de cada persona a tu
            cargo para que pueda participar en el evento.
          </p>
          <div className="flex flex-col gap-2">
            {items.map((it) => (
              <div
                key={it.registration.id}
                className="flex items-center justify-between gap-3 w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900 truncate">
                    {it.ward.fullName}
                  </p>
                  <p className="text-xs text-blue-600 truncate">
                    {it.event.name}
                  </p>
                </div>
                <button
                  onClick={() => setOpenId(it.registration.id)}
                  className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 shrink-0"
                >
                  Completar <ChevronRight className="w-4 h-4" />
                </button>
                <DirectorConsentLauncher
                  controlled
                  open={openId === it.registration.id}
                  onOpenChange={(v) => setOpenId(v ? it.registration.id : null)}
                  event={it.event}
                  registration={it.registration}
                  clubName={it.registration.clubName ?? ""}
                  associationName={associationName}
                  onCompleted={() => {
                    setOpenId(null);
                    setReloadTick((t) => t + 1);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
